//@flow
import React, { Component } from 'react';
import range from 'lodash/range';
import { Box, Flex, Text, Tooltip, Icon as NeoIcon } from '@locus-taxy/neo-ui';
import { NeoButton as Button } from 'common/components/neo/overrides';

import { dashboardColors } from 'common/enums/ColorEnum';
import { DivWithPopover } from 'common/components/core/wrappers/Div';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';
import { MenuItem } from 'common/components/core/menu';
import theme from './FpPlanViewPagination.scss';
import paginationIds from 'common/tests/common/pagination.json';
import { translate, type TFunction } from 'react-i18next';

type Props = {
    selectedPageIndex: number,
    totalPages: number,
    pageSize: number,
    totalItems: number,
    onPageSizeChange: number => void,
    onPageChange: number => void,
    t: TFunction
};

type State = {
    goToValue?: number,
    selectedOption: Object
};

class FpPlanViewPagination extends Component<Props, State> {
    state: State = {
        selectedOption: {},
        pageOptionsList: []
    };

    componentDidMount() {
        const { selectedPageIndex } = this.props;
        const pageOptionsList = this.computeItemsList();
        this.setState({
            pageOptionsList,
            selectedOption: pageOptionsList[selectedPageIndex]
        });
    }

    componentDidUpdate(prevProps) {
        const { selectedPageIndex, pageSize, totalItems } = this.props;
        if (selectedPageIndex !== prevProps.selectedPageIndex) {
            const start = selectedPageIndex * pageSize + 1;
            const end =
                (selectedPageIndex + 1) * pageSize > totalItems
                    ? totalItems
                    : (selectedPageIndex + 1) * pageSize;
            this.setState({
                selectedOption: { start, end }
            });
        }
    }

    PAGE_COUNT_OPTIONS = [10, 25, 50, 100];

    onNextClick = () => {
        const { selectedPageIndex, totalPages, onPageChange } = this.props;

        if (selectedPageIndex >= totalPages - 1) {
            //disabled
            return;
        }
        onPageChange(Math.min(totalPages - 1, selectedPageIndex + 1));
    };

    onBackClick = () => {
        const { selectedPageIndex, onPageChange } = this.props;
        if (selectedPageIndex <= 0) {
            //disabled
            return;
        }
        onPageChange(Math.max(0, selectedPageIndex - 1));
    };

    handlePageOptionSelect = (ev, menuItemProps) => {
        // Page size changer
        const { onPageSizeChange } = this.props;
        const { props } = menuItemProps;
        onPageSizeChange(props.value);
    };

    handlePageItemSelect = (ev, menuItemProps) => {
        // go to page
        const { onPageChange } = this.props;
        const { props } = menuItemProps;
        this.setState({ selectedOption: props.value });
        onPageChange(props.value.page);
    };

    computeItemsList = () => {
        const {
            selectedPageIndex,
            totalPages,
            pageSize,
            totalItems
        } = this.props;
        const itemsList = [];
        range(totalPages).forEach((item, index, arr) => {
            itemsList.push({
                page: item,
                start: pageSize * item + 1,
                end:
                    index === arr.length - 1
                        ? totalItems
                        : pageSize * (item + 1),
                icon:
                    selectedPageIndex === item ? (
                        <NeoIcon
                            icon="check"
                            size={NeoIcon.Size.medium}
                            color={'grey.600'}
                        />
                    ) : (
                        <Box mr={4} />
                    )
            });
        });
        return itemsList;
    };

    popoverItemsContent = () => {
        // Page count change
        const { pageSize, t, pageSizeOptions } = this.props;
        const pageOptions = pageSizeOptions || this.PAGE_COUNT_OPTIONS;
        return (
            <Box
                data-test-id={paginationIds.paginationMenu}
                backgroundColor={dashboardColors.white.code}
                width="120px"
            >
                {pageOptions.map((option, key) => (
                    <MenuItem
                        data-test-id={paginationIds.paginationMenuItem}
                        ripple={false}
                        className={theme.pageOptions}
                        theme={theme}
                        key={key}
                        icon={
                            pageSize === option ? (
                                <NeoIcon
                                    icon="check"
                                    size={NeoIcon.Size.medium}
                                    color={'grey.600'}
                                />
                            ) : (
                                <Box mr={4} />
                            )
                        }
                        value={option}
                        caption={
                            <Text fontSize={1} fontWeight="medium">
                                {t('itemCount', { count: option })}
                            </Text>
                        }
                        onClick={this.handlePageOptionSelect}
                    />
                ))}
            </Box>
        );
    };

    popoverContent = pageOptionsList => {
        // Selects page
        const { pageSize, t } = this.props;

        return (
            <Box backgroundColor={dashboardColors.white.code} width="184px">
                <Box p={1}>
                    <Text fontSize={1} fontWeight="medium" color={'grey.600'}>
                        {t('goTo')}
                    </Text>
                </Box>
                <Box
                    className={theme.pagesBox}
                    overflow="auto"
                    maxHeight="152px"
                >
                    {pageOptionsList.map((option, key) => (
                        <MenuItem
                            ripple={false}
                            key={key}
                            icon={option.icon}
                            value={option}
                            caption={
                                <Text
                                    fontSize={1}
                                    fontWeight="medium"
                                >{`${option.start} - ${option.end}`}</Text>
                            }
                            onClick={this.handlePageItemSelect}
                            className={theme.pages}
                            theme={theme}
                        />
                    ))}
                </Box>
                <Flex
                    position="absolute"
                    bottom={'0'}
                    height={'40px'}
                    width={'100%'}
                    className={theme.pageSizeChooserContainer}
                >
                    <Flex
                        width={'100%'}
                        alignItems="center"
                        justifyContent="space-between"
                        margin={3}
                    >
                        <Text
                            m={0}
                            fontSize={1}
                            fontWeight="medium"
                            color={'grey.600'}
                        >
                            {t('showUpTo')}
                        </Text>
                        <DivWithPopover
                            popOverContent={this.popoverItemsContent()}
                            triggerPopoverOn={PopoverTriggerEnum.Click}
                            place={PopoverPlaceEnum.Below}
                            invertPopover={false}
                            disableArrow
                        >
                            <Button
                                data-test-id={
                                    paginationIds.paginationDropdownBtn
                                }
                                backgroundColor="transparent"
                                appearance="ghost"
                            >
                                <Flex
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Text
                                        fontSize={1}
                                        m={0}
                                        fontWeight="medium"
                                    >
                                        {t('itemCount', {
                                            count: pageSize
                                        })}
                                    </Text>
                                    <NeoIcon
                                        icon="arrow_drop_down"
                                        size={NeoIcon.Size.Large}
                                        color={'grey.600'}
                                    />
                                </Flex>
                            </Button>
                        </DivWithPopover>
                    </Flex>
                </Flex>
            </Box>
        );
    };

    render() {
        const { totalItems, selectedPageIndex, totalPages, t } = this.props;
        const isPreviousDisabled = selectedPageIndex < 1;
        const pageOptionsList = this.computeItemsList();
        const selectedOption = pageOptionsList[selectedPageIndex];
        const isNextDisabled = selectedPageIndex >= totalPages - 1;
        return (
            <Flex alignItems="center">
                <Box>
                    <DivWithPopover
                        popOverContent={this.popoverContent(pageOptionsList)}
                        triggerPopoverOn={PopoverTriggerEnum.Click}
                        place={PopoverPlaceEnum.Below}
                        invertPopover={false}
                        disableArrow
                    >
                        <Button
                            data-test-id={paginationIds.paginationBtn}
                            backgroundColor="transparent"
                            appearance="ghost"
                        >
                            {selectedOption ? (
                                <Text
                                    fontSize={1}
                                    fontWeight="medium"
                                    color={'grey.600'}
                                    m={0}
                                >
                                    {`${selectedOption.start} - ${selectedOption.end} of ${totalItems}`}
                                </Text>
                            ) : null}
                        </Button>
                    </DivWithPopover>
                </Box>
                <Flex>
                    <Tooltip tooltip={t('pagination.previousPage')}>
                        <Box>
                            <Button
                                data-test-id={paginationIds.paginationPrevBtn}
                                appearance="icon"
                                icon="arrow_chevron_left"
                                color="grey.600"
                                backgroundColor="transparent"
                                onClick={this.onBackClick}
                                disabled={isPreviousDisabled}
                            />
                        </Box>
                    </Tooltip>
                    <Tooltip tooltip={t('pagination.nextPage')}>
                        <Box>
                            <Button
                                data-test-id={paginationIds.paginationNextBtn}
                                appearance="icon"
                                icon="arrow_chevron_right"
                                color="grey.600"
                                backgroundColor="transparent"
                                onClick={this.onNextClick}
                                disabled={isNextDisabled}
                            />
                        </Box>
                    </Tooltip>
                </Flex>
            </Flex>
        );
    }
}

export default translate('planner')(FpPlanViewPagination);
